/**
 * DEV-ONLY: Mock EventCalls injection for ProgressIndicator testing.
 *
 * Simulates MT's EventCalls behavior where event messages arrive
 * BEFORE the actual answer text during streaming.
 *
 * Alternates between two cases:
 * - First question: single event message (matches current MT behavior)
 * - Second question: multiple event messages (future MT behavior)
 * - Third question: single again, and so on...
 *
 * Enable via URL parameter: &mockEventCalls=true
 *
 * Remove this file (and the import in conversation-thread.tsx) once the
 * real MT contract ships EventCalls.
 */

import { type Activity } from 'botframework-directlinejs';
import { IEventCall, IBotOutgoingMessageValue, StreamingStatusType } from './botTypes';

/**
 * Checks whether mock EventCalls injection is enabled via URL query parameter.
 * Add &mockEventCalls=true to the URL to enable.
 * @param {string} search - URL search string (defaults to window.location.search)
 * @returns {boolean} True if mockEventCalls=true is present in the URL
 */
export function isMockEventCallsEnabled(search: string = window.location.search): boolean {
  return new URLSearchParams(search).get('mockEventCalls') === 'true';
}

/** Extended value type that includes StreamingStatus */
type StreamingMessageValue = IBotOutgoingMessageValue & {
  StreamingStatus?: string;
};

/**
 * Case 1: Single event message — matches current MT behavior.
 */
const SINGLE_EVENT: IEventCall[] = [
  {
    EventName: 'AdsCopilot.AnalyzeIntent',
    EventTimeStamp: '',
    EventMessage: '# Understanding your question\n- Identifying the key topics in your request.',
    EventMetaData: null,
  },
];

/**
 * Case 2: Multiple event messages — simulates future MT behavior
 * where multiple events accumulate progressively.
 */
const MULTI_EVENT_SNAPSHOTS: Array<IEventCall[]> = [
  // Snapshot 0: first event arrives
  [
    {
      EventName: 'AdsCopilot.AnalyzeIntent',
      EventTimeStamp: '',
      EventMessage: '# Understanding your question\n- Identifying the key topics in your request.',
      EventMetaData: null,
    },
  ],
  // Snapshot 1: second event arrives (accumulated)
  [
    {
      EventName: 'AdsCopilot.AnalyzeIntent',
      EventTimeStamp: '',
      EventMessage: '# Understanding your question\n- Identifying the key topics in your request.',
      EventMetaData: null,
    },
    {
      EventName: 'AdsCopilot.QueryData',
      EventTimeStamp: '',
      EventMessage: '# Querying campaign data\n- Searching for relevant campaigns and ad groups.\n- Pulling impressions, clicks, and conversions.',
      EventMetaData: null,
    },
  ],
  // Snapshot 2: third event arrives (accumulated)
  [
    {
      EventName: 'AdsCopilot.AnalyzeIntent',
      EventTimeStamp: '',
      EventMessage: '# Understanding your question\n- Identifying the key topics in your request.',
      EventMetaData: null,
    },
    {
      EventName: 'AdsCopilot.QueryData',
      EventTimeStamp: '',
      EventMessage: '# Querying campaign data\n- Searching for relevant campaigns and ad groups.\n- Pulling impressions, clicks, and conversions.',
      EventMetaData: null,
    },
    {
      EventName: 'AdsCopilot.GenerateInsights',
      EventTimeStamp: '',
      EventMessage: '# Generating insights\n- Summarizing top-performing campaigns.\n- Highlighting areas needing attention.',
      EventMetaData: null,
    },
  ],
];

/** Minimum seconds between progressing to the next snapshot (multi-event mode) */
const COOLDOWN_SECONDS = 3;

/**
 * Per-reply state tracking.
 */
interface ReplyState {
  snapshotIndex: number;
  lastAdvanceTime: number;
  baseTime: number;
  isMulti: boolean;
}
const replyStates: Record<string, ReplyState> = {};

/** Tracks how many unique replies we've seen to alternate single/multi */
let replyCount = 0;

/**
 * Gets the current EventCalls for a reply.
 * Alternates between single and multi event cases per question.
 * @param {string} replyToId - The reply ID to track state
 * @returns {IEventCall[]} Array of event calls for this reply
 */
function getEventCalls(replyToId: string): IEventCall[] {
  const now = Date.now();

  if (!replyStates[replyToId]) {
    // Alternate: even = single, odd = multi
    const isMulti = replyCount % 2 === 1;
    replyCount += 1;

    replyStates[replyToId] = {
      snapshotIndex: 0,
      lastAdvanceTime: now,
      baseTime: now,
      isMulti,
    };
  }

  const state = replyStates[replyToId];

  if (!state.isMulti) {
    // Single event mode: always return one event
    return SINGLE_EVENT.map(event => ({
      ...event,
      EventTimeStamp: new Date(now).toISOString(),
    }));
  }

  // Multi event mode: progress through snapshots
  if (state.snapshotIndex < MULTI_EVENT_SNAPSHOTS.length - 1) {
    const elapsed = (now - state.lastAdvanceTime) / 1000;
    if (elapsed >= COOLDOWN_SECONDS) {
      state.snapshotIndex += 1;
      state.lastAdvanceTime = now;
    }
  }

  const snapshot = MULTI_EVENT_SNAPSHOTS[state.snapshotIndex];

  return snapshot.map((event, i) => ({
    ...event,
    EventTimeStamp: new Date(state.baseTime + (state.snapshotIndex * COOLDOWN_SECONDS * 1000) + (i * 500)).toISOString(),
  }));
}

/**
 * Injects mock EventCalls into a bot streaming activity's value object.
 *
 * Only injects during streaming (InProgress) — not after completion.
 * Alternates between single event and multi event cases per question.
 *
 * Mutates the activity in place.
 */
// eslint-disable-next-line no-param-reassign
export function injectMockEventCalls(activity: Activity): void {
  if (activity.type !== 'message') return;

  const value = (activity as any).value as StreamingMessageValue | string | undefined;

  if (!value || typeof value === 'string' || value.StreamingStatus === undefined) {
    return;
  }

  // Don't override real EventCalls if MT starts sending them
  if (value.EventCalls && value.EventCalls.length > 0) {
    return;
  }

  // Only inject during streaming — not after completion
  if (value.StreamingStatus === StreamingStatusType.Completed
    || value.StreamingStatus === StreamingStatusType.Cancelled
    || value.StreamingStatus === '') {
    return;
  }

  const replyToId = (activity as any).replyToId || activity.id || 'unknown';
  const eventCalls = getEventCalls(replyToId);

  // eslint-disable-next-line no-param-reassign
  (activity as any).value = {
    ...value,
    EventCalls: eventCalls,
  };
}
